import { ClientFactory } from "@/main/common/ClientFactory";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { ContentReportBody } from "@/main/common/ReportTypes";

export type ModerationReportReasonType =
    | "SPAM"
    | "HARASSMENT"
    | "HATE_SPEECH"
    | "ABUSE"
    | "SEXUAL_CONTENT"
    | "CSAE"
    | "VIOLENCE_OR_THREATS"
    | "ILLEGAL_CONTENT"
    | "FRAUD_OR_IMPERSONATION"
    | "PRIVACY_VIOLATION"
    | "SELF_HARM"
    | "RULE_VIOLATION"
    | "COPYRIGHT"
    | "AI_SLOP"
    | "OTHER";

export type ReportableEntityType =
    | "discussion"
    | "argument"
    | "group"
    | "series"
    | "meme_template"
    | "meme"
    | "chat_message"
    | "group_chat_message";

/**
 * The reasons hidden when reporting a direct message.
 *
 * A FILTER over the one ordered array below, never a second list: a reason added later appears in both
 * surfaces by default, which is the whole point. Both of these are meaningless for a private message
 * between two people, and offering them produces noise a moderator has to triage.
 */
const REASONS_HIDDEN_FOR_CHAT: ReadonlySet<ModerationReportReasonType> = new Set<ModerationReportReasonType>([
    "AI_SLOP",
    "COPYRIGHT",
]);

// CSAE is deliberately NOT in that set and must never be added to it. A private message from an adult
// stranger is the surface where the harm this reason names actually happens, and it is the one a
// 15-year-old was reached through on 2026-08-19. Hiding it from DMs would remove the reason from the
// only place it matters most.

/**
 * The reason list for one report surface, in the canonical order.
 *
 * Every surface goes through here so the ordering, the default and any future addition stay shared.
 */
export function reportReasonTypesFor(entityType: ReportableEntityType): readonly ModerationReportReasonType[] {
    // `group_chat_message` deliberately falls into the FULL list, and the narrow test below is what
    // keeps it there. It looks like an oversight beside its DM sibling and is not: the two reasons
    // hidden for a DM are hidden because they are meaningless between two people, and a room of
    // hundreds is where "this is AI slop" and "this is copyrighted" become ordinary complaints again.
    // Widening this condition to cover both chat kinds would remove two real reasons from a public
    // surface.
    if (entityType !== "chat_message") {
        return MODERATION_REPORT_REASON_TYPES;
    }
    return MODERATION_REPORT_REASON_TYPES.filter((value) => !REASONS_HIDDEN_FOR_CHAT.has(value));
}

// Neutral default: a one-click submit should not silently mislabel every report as SPAM.
// Reporters who want a specific category still pick one; moderators get an honest "uncategorized"
// instead of a false spam accusation.
export const DEFAULT_REPORT_REASON_TYPE: ModerationReportReasonType = "OTHER";

export const MODERATION_REPORT_REASON_TYPES: readonly ModerationReportReasonType[] = [
    "SPAM",
    "HARASSMENT",
    "HATE_SPEECH",
    "ABUSE",
    "SEXUAL_CONTENT",
    // Child sexual abuse and exploitation. Sits next to SEXUAL_CONTENT, matching the backend
    // ContentReportReasonType ordering, because the two are what a reporter is choosing between.
    "CSAE",
    "VIOLENCE_OR_THREATS",
    "ILLEGAL_CONTENT",
    "FRAUD_OR_IMPERSONATION",
    "PRIVACY_VIOLATION",
    "SELF_HARM",
    "RULE_VIOLATION",
    "COPYRIGHT",
    "AI_SLOP",
    "OTHER",
] as const;

export const MODERATION_REPORT_REASON_LOCALIZATION_MAP: Record<ModerationReportReasonType, LocalizationKey> = {
    SPAM: "report.modal.reason.spam",
    HARASSMENT: "report.modal.reason.harassment",
    HATE_SPEECH: "report.modal.reason.hate_speech",
    ABUSE: "report.modal.reason.abuse",
    SEXUAL_CONTENT: "report.modal.reason.sexual_content",
    CSAE: "report.modal.reason.csae",
    VIOLENCE_OR_THREATS: "report.modal.reason.violence_or_threats",
    ILLEGAL_CONTENT: "report.modal.reason.illegal_content",
    FRAUD_OR_IMPERSONATION: "report.modal.reason.fraud_or_impersonation",
    PRIVACY_VIOLATION: "report.modal.reason.privacy_violation",
    SELF_HARM: "report.modal.reason.self_harm",
    RULE_VIOLATION: "report.modal.reason.rule_violation",
    COPYRIGHT: "report.modal.reason.copyright",
    AI_SLOP: "report.modal.reason.ai_slop",
    OTHER: "report.modal.reason.other",
};

/**
 * The one-line gloss under each reason in the picker.
 *
 * <p><b>`CSAE` is deliberately absent, and the type says so.</b> Every other reason gets a gloss
 * because its label is ambiguous ("Abuse", "Other", "Rule violation" all need one). "Child sexual
 * abuse or exploitation" does not: it is the least ambiguous label in the list. The reason it is
 * absent rather than merely unnecessary is the locale-catalog ceiling — the largest catalog (Greek)
 * sits within a few hundred bytes of `check-bundle-budget`'s limit, one catalog is modulepreloaded on
 * every page, and a two-line description across 25 catalogs does not fit. Decision recorded in
 * `designs/01-now/2026-08-19-user-safety-phase-0-design.md`; it reverses when the per-route catalog
 * split lands.
 */
export const MODERATION_REPORT_REASON_DESCRIPTION_LOCALIZATION_MAP: Partial<Record<ModerationReportReasonType, LocalizationKey>> = {
    SPAM: "report.modal.reason_description.spam",
    HARASSMENT: "report.modal.reason_description.harassment",
    HATE_SPEECH: "report.modal.reason_description.hate_speech",
    ABUSE: "report.modal.reason_description.abuse",
    SEXUAL_CONTENT: "report.modal.reason_description.sexual_content",
    VIOLENCE_OR_THREATS: "report.modal.reason_description.violence_or_threats",
    ILLEGAL_CONTENT: "report.modal.reason_description.illegal_content",
    FRAUD_OR_IMPERSONATION: "report.modal.reason_description.fraud_or_impersonation",
    PRIVACY_VIOLATION: "report.modal.reason_description.privacy_violation",
    SELF_HARM: "report.modal.reason_description.self_harm",
    RULE_VIOLATION: "report.modal.reason_description.rule_violation",
    COPYRIGHT: "report.modal.reason_description.copyright",
    AI_SLOP: "report.modal.reason_description.ai_slop",
    OTHER: "report.modal.reason_description.other",
};

export const REPORTABLE_ENTITY_LOCALIZATION_MAP: Record<ReportableEntityType, LocalizationKey> = {
    discussion: "report.entity.discussion",
    argument: "report.entity.thought",
    group: "report.entity.group",
    series: "report.entity.series",
    meme_template: "report.entity.meme_template",
    meme: "report.entity.meme",
    chat_message: "report.entity.chat_message",
    // The SAME key, not a sibling of it. `report.entity.chat_message` is the single word "message",
    // which is what this label renders into ("Report this message"), and it is already correct in all
    // 25 catalogs for both chat kinds. Minting `report.entity.group_chat_message` would have bought a
    // 25-locale translation pass to say the same word twice, and two keys for one string drift.
    group_chat_message: "report.entity.chat_message",
};

// The report endpoints answer 201 CREATED for a newly filed report and 200 OK when the reporter
// already has an open report for that target. Surfacing that lets the UI say "we already have your
// report" instead of pretending each duplicate submit is a fresh one.
function isNewlyCreated(status: number): boolean {
    return status === 201;
}

/**
 * Builds the request body.
 *
 * `attachmentImagePublicIds` is OMITTED rather than sent as `[]` when there is nothing to attach, so a
 * client running against a server that predates attachments sends a byte-identical payload to what it
 * always sent. The two deploy halves are therefore order-independent, and the failure mode of getting
 * them out of order is a missing capability rather than a rejected report.
 */
function buildBody(
    reason: string,
    reasonType: ModerationReportReasonType,
    attachmentImagePublicIds?: string[],
): ContentReportBody {
    const body: ContentReportBody = { reason, reasonType };
    if (attachmentImagePublicIds && attachmentImagePublicIds.length > 0) {
        body.attachmentImagePublicIds = attachmentImagePublicIds;
    }
    return body;
}

// All eight go through hand-written ClientFactory wrappers over the shared axios instance rather than
// the generated client (AGENTS.md rule 6): the request DTO gained a field the committed generated
// client does not know about, and regenerating it needs a running local backend. The reason type is
// sent by NAME, which is what the enum's wire form has always been.
export const ModerationReportApi = {
    async reportDiscussion(discussionPublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportDiscussion(discussionPublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    async reportThought(argumentPublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportThought(argumentPublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    async reportGroup(groupPublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportGroup(groupPublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    /** FI-039: a series is reported as a series, with the group's reason set (settle A1/A2). */
    async reportSeries(seriesPublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportSeries(seriesPublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    async reportMemeTemplate(templatePublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportMemeTemplate(templatePublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    async reportMeme(memePublicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        const response = await ClientFactory.reportMeme(memePublicId, buildBody(reason, reasonType, attachmentImagePublicIds));
        return isNewlyCreated(response.status);
    },

    /**
     * Report one received direct message, optionally blocking its sender in the same action.
     *
     * Takes `block` where the six reporters above take attachments, and that asymmetry is the contract: a DM
     * report offers no screenshot picker because the evidence is the conversation, which the server
     * captures verbatim. See `ChatMessageReportBody`.
     */
    async reportChatMessage(messagePublicId: string, reason: string, reasonType: ModerationReportReasonType, block: boolean): Promise<boolean> {
        const response = await ClientFactory.reportChatMessage(messagePublicId, { reason, reasonType, block });
        return isNewlyCreated(response.status);
    },

    /**
     * Report one message in a group room.
     *
     * Takes neither `block` nor attachments, and both omissions are the contract. There is no block
     * half because the room analogue of blocking is banning, which is a leader's power rather than a
     * reporter's. There is no screenshot picker because the evidence is the conversation, which the
     * server captures verbatim — the same reason the DM report has none.
     */
    async reportGroupChatMessage(messagePublicId: string, reason: string, reasonType: ModerationReportReasonType): Promise<boolean> {
        const response = await ClientFactory.reportGroupChatMessage(messagePublicId, buildBody(reason, reasonType));
        return isNewlyCreated(response.status);
    },

    // Returns true when a new report was filed, false when the reporter already had an open report.
    async report(entityType: ReportableEntityType, publicId: string, reason: string, reasonType: ModerationReportReasonType, attachmentImagePublicIds?: string[]): Promise<boolean> {
        switch (entityType) {
            case "discussion":
                return this.reportDiscussion(publicId, reason, reasonType, attachmentImagePublicIds);
            case "argument":
                return this.reportThought(publicId, reason, reasonType, attachmentImagePublicIds);
            case "group":
                return this.reportGroup(publicId, reason, reasonType, attachmentImagePublicIds);
            case "series":
                return this.reportSeries(publicId, reason, reasonType, attachmentImagePublicIds);
            case "meme_template":
                return this.reportMemeTemplate(publicId, reason, reasonType, attachmentImagePublicIds);
            case "meme":
                return this.reportMeme(publicId, reason, reasonType, attachmentImagePublicIds);
            case "chat_message":
                // Reached only through the dialog's chat branch, which passes `block` explicitly. This
                // arm exists so the switch stays exhaustive; a DM reported through the generic entry
                // point has said nothing about blocking, and NOT blocking is the safe default.
                return this.reportChatMessage(publicId, reason, reasonType, false);
            case "group_chat_message":
                // Unlike the DM arm above this is the ONLY entry point, not a fallback for one: the
                // dialog has no group-chat branch to bypass it, because there is no block half to
                // collect. Attachments are dropped rather than forwarded for the same reason the DM
                // arm drops them — the evidence is the captured window, not a screenshot.
                return this.reportGroupChatMessage(publicId, reason, reasonType);
        }
    },
};
